/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { ticketColors } from '../../shared/theme/ticketColors'
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts';
import { colors } from '../../shared/theme/colors';
import { webTextInputAutofillStyle } from '../../shared/theme/webTextInput';
import { WEB_SIDEBAR_BREAKPOINT } from '../../shared/layout/breakpoints';
import { ModalScrollScreen } from '../../shared/layout/ModalScrollScreen';
import { AuthScreenFrame } from '../../shell/brand/AuthScreenFrame';
import { AppLogoMark } from '../../shell/brand/AppLogoMark';
import { EmailConfirmationPendingScreen } from './EmailConfirmationPendingScreen';
import { useAuthStore } from '../store/useAuthStore';
import { openLegalUrl } from '../../shared/legalUrls';
import { showInfo } from '../../shared/ui/appToast';
import { AUTH_ACCENT_INK, authFormUi } from './components/authFormUi';
import { AuthWhatsAppHelpLink } from './components/AuthWhatsAppHelpLink';
import { AuthSsoSection } from './components/AuthSsoSection';
import { LoginValuePanel } from './components/LoginValuePanel';
import { LOGIN_VALUE_TITLE } from './components/loginMarketingCopy';
import { useLoginForm } from '../hooks/useLoginForm';
import { useSignupForm } from '../hooks/useSignupForm';
import { usePasswordResetFromLogin } from '../hooks/usePasswordResetFromLogin';
import {
  peekMarketingPlanIntent,
} from '../../shell/feature-flags/planIntentService';
import { creceLoginCopy } from '../../../../../shared/plan-crece-copy';
import { trackCreceFunnel } from '../../shell/billing/trackCreceFunnel';
import { CRECE_FUNNEL_EVENTS } from '../../../../../shared/crece-funnel';
import { isBillingEnabled } from '../../shell/billing/isBillingEnabled';

export const LoginScreen = () => {
  const { width } = useWindowDimensions();
  const showValuePanel = width >= WEB_SIDEBAR_BREAKPOINT;
  const { loading, error } = useAuthStore();
  const hasCreceIntent = Boolean(isBillingEnabled() && peekMarketingPlanIntent());
  const [authMode, setAuthMode] = useState<'login' | 'signup'>(
    hasCreceIntent ? 'signup' : 'login',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const passwordInputRef = useRef<TextInput>(null);
  const passwordConfirmRef = useRef<TextInput>(null);
  const emailInputRef = useRef<TextInput>(null);
  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const scrollContentRef = useRef<View>(null);

  const [loginConfirmationPendingEmail, setLoginConfirmationPendingEmail] = useState<string | null>(
    null,
  );
  const { handleLogin } = useLoginForm({
    email,
    password,
    onEmailConfirmationRequired: setLoginConfirmationPendingEmail,
  });
  const { handleSignup, confirmationPendingEmail, clearConfirmationPending } = useSignupForm({
    email,
    password,
    passwordConfirm,
  });
  const emailAwaitingConfirmation =
    confirmationPendingEmail ?? loginConfirmationPendingEmail;
  const { handleReset, resetSending } = usePasswordResetFromLogin({ email });

  useEffect(() => {
    ensureTicketFonts();
  }, []);

  useEffect(() => {
    if (!hasCreceIntent) return;
    trackCreceFunnel(CRECE_FUNNEL_EVENTS.authView);
  }, [hasCreceIntent]);

  const isSignup = authMode === 'signup';
  const passwordOk = password.length >= 8;
  const clearError = () => useAuthStore.getState().setError(undefined);

  const handleEmailChange = (text: string) => {
    setEmail(text);
    clearError();
  };

  const handlePasswordChange = (text: string) => {
    setPassword(text);
    clearError();
  };

  /** Login y signup comparten los mismos campos; despacha según el modo. */
  const handleSubmit = () => (isSignup ? handleSignup() : handleLogin());

  const loginDisabled =
    loading ||
    !email.trim() ||
    !password ||
    (isSignup && (!passwordConfirm || password !== passwordConfirm || !passwordOk));

  const title = isSignup ? 'Creá tu cuenta' : 'Ingresá a tu negocio';
  /** Login sigue proto C; signup mantiene chrome ticket. */
  const protoLogin = !isSignup;
  const subtitle = isSignup
    ? 'Si sos dueño, en unos minutos armás el local.'
    : showValuePanel
      ? 'Entrá con Google o con el correo de tu cuenta.'
      : LOGIN_VALUE_TITLE;

  const showInviteHint = () => {
    showInfo(
      'Te invitaron a un local',
      'Te llega un mail de Vuelto. Abrí ese enlace (no hace falta este botón). Después ingresá acá con el mismo correo.',
    );
  };

  if (emailAwaitingConfirmation) {
    return (
      <EmailConfirmationPendingScreen
        email={emailAwaitingConfirmation}
        onBackToLogin={() => {
          clearConfirmationPending();
          setLoginConfirmationPendingEmail(null);
          setAuthMode('login');
          setPassword('');
          setPasswordConfirm('');
          clearError();
        }}
      />
    );
  }

  const formCard = (
    <View
      style={[
        protoLogin
          ? showValuePanel
            ? styles.formPlain
            : styles.formMobile
          : authFormUi.card,
        protoLogin && showValuePanel ? styles.formPlainWide : null,
      ]}
    >
      {protoLogin && !showValuePanel ? (
        <View style={styles.logoRow} accessibilityRole="header">
          <View style={styles.logoMark} accessibilityElementsHidden>
            <Text style={styles.logoMarkLetter}>V</Text>
          </View>
          <Text style={styles.logoWordmark}>Vuelto</Text>
        </View>
      ) : null}

      {!protoLogin ? (
        <View style={authFormUi.brandBlock}>
          <AppLogoMark size={56} showWordmark />
        </View>
      ) : null}

      {isSignup && !hasCreceIntent ? (
        <TouchableOpacity
          style={authFormUi.backBtn}
          onPress={() => {
            setAuthMode('login');
            clearError();
          }}
          disabled={loading}
          accessibilityRole="button"
          accessibilityLabel="Volver a ingresar"
        >
          <Text style={authFormUi.backLabel}>← Volver</Text>
        </TouchableOpacity>
      ) : null}

      <View style={styles.head}>
        <Text style={[authFormUi.title, protoLogin ? styles.formTitlePlain : null]}>{title}</Text>
        <Text style={[authFormUi.subtitle, protoLogin ? styles.formSubPlain : null]}>{subtitle}</Text>
      </View>

      {hasCreceIntent ? (
        <View
          style={authFormUi.inviteHintBanner}
          accessibilityRole="text"
          accessibilityLabel={`${creceLoginCopy.bannerTitle}. ${creceLoginCopy.bannerBody}`}
        >
          <Text style={authFormUi.inviteHintBannerText}>
            {creceLoginCopy.bannerTitle}. {creceLoginCopy.bannerBody}
          </Text>
        </View>
      ) : null}

      {hasCreceIntent ? (
        <View style={styles.modeTabs}>
          {(['signup', 'login'] as const).map((mode) => {
            const active = authMode === mode;
            const label = mode === 'signup' ? creceLoginCopy.tabSignup : creceLoginCopy.tabLogin;
            return (
              <TouchableOpacity
                key={mode}
                style={[styles.modeTab, active && styles.modeTabActive]}
                onPress={() => {
                  setAuthMode(mode);
                  clearError();
                }}
                disabled={loading || active}
                accessibilityRole="button"
                accessibilityState={{ selected: active }}
                accessibilityLabel={label}
                testID={mode === 'signup' ? 'auth.tab.signup' : 'auth.tab.login'}
              >
                <Text style={[styles.modeTabText, active && styles.modeTabTextActive]}>
                  {label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      ) : null}

      {isSignup ? (
        <View
          style={authFormUi.inviteHintBanner}
          accessibilityRole="text"
          accessibilityLabel="Si te van a invitar a un local, no te registres acá. Esperá el mail de invitación."
        >
          <Text style={authFormUi.inviteHintBannerText}>
            ¿Te van a invitar a un local? No te registres acá — esperá el mail.
          </Text>
        </View>
      ) : null}

      <AuthSsoSection disabled={loading} />

      <View style={authFormUi.fieldGroup}>
        <Text style={[authFormUi.label, protoLogin ? styles.labelPlain : null]}>Correo</Text>
        <TextInput
          ref={emailInputRef}
          value={email}
          onChangeText={handleEmailChange}
          autoCapitalize="none"
          autoComplete="email"
          keyboardType="email-address"
          returnKeyType="next"
          blurOnSubmit={false}
          onSubmitEditing={() => passwordInputRef.current?.focus()}
          placeholder="tu@correo.com"
          placeholderTextColor={ticketColors.ink3}
          style={[
            authFormUi.input,
            protoLogin ? styles.inputPlain : null,
            error ? authFormUi.inputError : null,
            webTextInputAutofillStyle(),
          ]}
          accessibilityLabel="Correo"
          accessibilityHint="Ingresá tu correo electrónico"
          testID="auth.email"
        />
      </View>

      <View style={authFormUi.fieldGroup}>
        <Text style={[authFormUi.label, protoLogin ? styles.labelPlain : null]}>Contraseña</Text>
        <View style={authFormUi.passwordRow}>
          <TextInput
            ref={passwordInputRef}
            value={password}
            onChangeText={handlePasswordChange}
            autoComplete={isSignup ? 'password-new' : 'password'}
            textContentType={isSignup ? 'newPassword' : 'password'}
            secureTextEntry={!passwordVisible}
            returnKeyType={isSignup ? 'next' : 'go'}
            blurOnSubmit={!isSignup}
            onSubmitEditing={() => {
              if (loading) return;
              if (isSignup) {
                passwordConfirmRef.current?.focus();
              } else {
                void handleSubmit();
              }
            }}
            placeholder={isSignup ? 'Mínimo 8 caracteres' : '••••••••'}
            placeholderTextColor={ticketColors.ink3}
            style={[
              authFormUi.input,
              authFormUi.passwordInput,
              protoLogin ? styles.inputPlain : null,
              error ? authFormUi.inputError : null,
              webTextInputAutofillStyle(),
            ]}
            accessibilityLabel="Contraseña"
            accessibilityHint="Ingresá tu contraseña"
            testID="auth.password"
          />
          <TouchableOpacity
            style={authFormUi.eyeButton}
            onPress={() => setPasswordVisible((v) => !v)}
            accessibilityLabel={passwordVisible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            accessibilityRole="button"
          >
            <Ionicons
              name={passwordVisible ? 'eye-off-outline' : 'eye-outline'}
              size={20}
              color={ticketColors.ink3}
            />
          </TouchableOpacity>
        </View>
        {isSignup ? (
          <View style={authFormUi.passwordHint}>
            <View style={[authFormUi.passwordHintDot, passwordOk && authFormUi.passwordHintDotOk]} />
            <Text style={[authFormUi.passwordHintText, passwordOk && authFormUi.passwordHintTextOk]}>
              Al menos 8 caracteres
            </Text>
          </View>
        ) : null}
      </View>

      {isSignup ? (
        <View style={authFormUi.fieldGroup}>
          <Text style={authFormUi.label}>Repetí la contraseña</Text>
          <TextInput
            ref={passwordConfirmRef}
            value={passwordConfirm}
            onChangeText={(t) => {
              setPasswordConfirm(t);
              clearError();
            }}
            autoComplete="password-new"
            textContentType="newPassword"
            secureTextEntry={!passwordVisible}
            returnKeyType="go"
            blurOnSubmit
            onSubmitEditing={() => {
              if (!loading) void handleSubmit();
            }}
            placeholder="Volvé a escribirla"
            placeholderTextColor={ticketColors.ink3}
            style={[authFormUi.input, webTextInputAutofillStyle()]}
            accessibilityLabel="Repetí la contraseña"
          />
        </View>
      ) : null}

      {isSignup ? (
        <Text style={authFormUi.legal} accessibilityRole="text">
          Al registrarte aceptás los{' '}
          <Text
            style={authFormUi.legalLink}
            onPress={() => void openLegalUrl('terms')}
            accessibilityRole="link"
            accessibilityLabel="Abrir términos y condiciones"
          >
            términos
          </Text>{' '}
          y la{' '}
          <Text
            style={authFormUi.legalLink}
            onPress={() => void openLegalUrl('privacy')}
            accessibilityRole="link"
            accessibilityLabel="Abrir política de privacidad"
          >
            política de privacidad
          </Text>
          .
        </Text>
      ) : null}

      {error ? (
        <View style={authFormUi.errorBanner} accessibilityLiveRegion="polite" accessibilityLabel={error}>
          <Text style={authFormUi.errorBannerText}>{error}</Text>
        </View>
      ) : null}

      <TouchableOpacity
        style={[
          authFormUi.primaryBtn,
          protoLogin ? styles.primaryBrand : null,
          hasCreceIntent ? styles.primaryCrece : null,
          loginDisabled && (protoLogin ? styles.primaryBrandDisabled : authFormUi.primaryBtnDisabled),
        ]}
        onPress={handleSubmit}
        disabled={loginDisabled}
        accessibilityLabel={
          isSignup ? (hasCreceIntent ? creceLoginCopy.ctaSignup : 'Registrarme') : 'Ingresar'
        }
        accessibilityRole="button"
        accessibilityState={{ disabled: loginDisabled }}
        testID={isSignup ? 'auth.signup' : 'auth.login'}
      >
        {loading ? (
          <ActivityIndicator
            color={
              protoLogin
                ? loginDisabled
                  ? colors.textMuted
                  : '#FFFFFF'
                : loginDisabled
                  ? ticketColors.ink3
                  : AUTH_ACCENT_INK
            }
          />
        ) : (
          <Text
            style={[
              authFormUi.primaryLabel,
              protoLogin ? styles.primaryBrandLabel : null,
              loginDisabled &&
                (protoLogin ? styles.primaryBrandLabelDisabled : authFormUi.primaryLabelDisabled),
            ]}
          >
            {isSignup ? (hasCreceIntent ? creceLoginCopy.ctaSignup : 'Registrarme') : 'Ingresar'}
          </Text>
        )}
      </TouchableOpacity>

      <View style={[authFormUi.links, protoLogin ? styles.linksStart : null]}>
        {!hasCreceIntent ? (
          <TouchableOpacity
            onPress={() => {
              setAuthMode((m) => (m === 'login' ? 'signup' : 'login'));
              clearError();
            }}
            disabled={loading}
            accessibilityLabel={isSignup ? 'Cambiar a iniciar sesión' : 'Cambiar a abrir mi local'}
            accessibilityRole="button"
          >
            <Text style={[authFormUi.linkLabel, protoLogin ? styles.linkProto : null]}>
              {isSignup ? '¿Ya tenés cuenta? Ingresar' : '¿No tenés cuenta? Abrir mi local'}
            </Text>
          </TouchableOpacity>
        ) : null}

        {!isSignup ? (
          <TouchableOpacity
            onPress={handleReset}
            disabled={loading || resetSending}
            accessibilityLabel={resetSending ? 'Enviando correo de recuperación…' : '¿Olvidaste tu contraseña?'}
            accessibilityRole="button"
            accessibilityState={{ disabled: loading || resetSending, busy: resetSending }}
            testID="auth.forgot-password"
          >
            {resetSending ? (
              <View style={[styles.linkRow, protoLogin ? styles.linkRowStart : null]}>
                <ActivityIndicator size="small" color={ticketColors.ink2} />
                <Text style={[authFormUi.linkLabel, styles.linkProto]}>Enviando…</Text>
              </View>
            ) : (
              <Text style={[authFormUi.linkLabel, styles.linkProto]}>¿Olvidaste tu contraseña?</Text>
            )}
          </TouchableOpacity>
        ) : null}

        {!isSignup ? (
          <TouchableOpacity
            onPress={showInviteHint}
            accessibilityLabel="¿Te invitaron? Abrí el link del mail"
            accessibilityRole="button"
            testID="auth.invite-hint"
          >
            <Text style={[authFormUi.linkLabelMuted, styles.linkProtoMuted]}>
              ¿Te invitaron? Abrí el link del mail
            </Text>
          </TouchableOpacity>
        ) : null}

        {!isSignup ? <AuthWhatsAppHelpLink /> : null}
      </View>
    </View>
  );

  return (
    <AuthScreenFrame
      showWatermark={false}
      backgroundColor={showValuePanel ? '#FFFFFF' : colors.background}
    >
      {showValuePanel ? (
        <View style={styles.splitRoot}>
          <LoginValuePanel />
          <View style={styles.splitFormCol}>
            <ModalScrollScreen
              ref={scrollRef}
              contentRef={scrollContentRef}
              variant="auth"
              contentContainerStyle={styles.splitScroll}
              keyboardShouldPersistTaps="handled"
            >
              {formCard}
            </ModalScrollScreen>
          </View>
        </View>
      ) : (
        <ModalScrollScreen
          ref={scrollRef}
          contentRef={scrollContentRef}
          variant="auth"
          contentContainerStyle={[authFormUi.scrollCenter, styles.mobileScroll]}
          keyboardShouldPersistTaps="handled"
        >
          {formCard}
        </ModalScrollScreen>
      )}
    </AuthScreenFrame>
  );
};

const styles = StyleSheet.create({
  head: {
    gap: 6,
  },
  modeTabs: {
    flexDirection: 'row',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.controlBorder,
    backgroundColor: colors.surfaceSecondary,
    padding: 3,
    gap: 3,
  },
  modeTab: {
    flex: 1,
    borderRadius: 9,
    paddingVertical: 9,
    alignItems: 'center',
  },
  modeTabActive: {
    backgroundColor: '#FFFFFF',
  },
  modeTabText: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink3,
  },
  modeTabTextActive: {
    color: ticketColors.ink,
    fontWeight: '800',
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  linkRowStart: {
    justifyContent: 'flex-start',
  },
  linksStart: {
    alignItems: 'flex-start',
  },
  linkProto: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  linkProtoMuted: {
    fontSize: 13,
    fontWeight: '500',
    color: ticketColors.ink3,
  },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  logoMark: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: colors.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoMarkLetter: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
  logoWordmark: {
    fontSize: 18,
    fontWeight: '800',
    letterSpacing: -0.3,
    color: ticketColors.ink,
  },
  mobileScroll: {
    paddingHorizontal: 20,
  },
  formMobile: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
    gap: 14,
  },
  splitRoot: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
  },
  splitFormCol: {
    flex: 1,
    minWidth: 360,
    backgroundColor: '#FFFFFF',
  },
  splitScroll: {
    flexGrow: 1,
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    minHeight: '100%',
    paddingTop: 36,
    paddingBottom: 36,
    paddingHorizontal: 32,
    backgroundColor: '#FFFFFF',
  },
  formPlain: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
    gap: 14,
    backgroundColor: '#FFFFFF',
  },
  formPlainWide: {
    gap: 14,
  },
  formTitlePlain: {
    fontSize: 22,
    letterSpacing: -0.4,
    fontWeight: '800',
  },
  formSubPlain: {
    marginTop: 0,
    fontSize: 14,
    lineHeight: 20,
    color: ticketColors.ink2,
  },
  labelPlain: {
    fontSize: 13,
    fontWeight: '600',
    letterSpacing: 0,
    textTransform: 'none',
  },
  inputPlain: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderColor: colors.controlBorder,
  },
  primaryBrand: {
    backgroundColor: colors.accent,
    borderRadius: 999,
  },
  /** Journey Crece: el CTA promete continuar al pago — acento naranja del proto. */
  primaryCrece: {
    backgroundColor: ticketColors.crece,
  },
  primaryBrandDisabled: {
    backgroundColor: colors.surfaceSecondary,
    borderWidth: 0,
    opacity: 1,
  },
  primaryBrandLabel: {
    color: '#FFFFFF',
  },
  primaryBrandLabelDisabled: {
    color: colors.textMuted,
  },
});
